import React, { useState } from "react";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import "./FileList.css";
import { getSignedFileUrl, deleteOrganizationFile, downloadOrganizationFile } from "../../Utils/functions";

const FileList = ({ files, onRemove, onRefresh, isAdmin }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingIdx, setDeletingIdx] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const handleMenuOpen = (event, idx) => {
    setAnchorEl(event.currentTarget);
    setSelectedIdx(idx);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedIdx(null);
  };

  const handleView = async (file) => {
    handleMenuClose();
    const orgId = localStorage.getItem("organizationId");
    if (!orgId) return;
    try {
      const url = await getSignedFileUrl(orgId, file.id);
      if (url) {
        window.open(url, "_blank");
        setSnackbar({ open: true, message: "File opened in new tab.", severity: "success" });
      } else {
        setSnackbar({ open: true, message: "File not found.", severity: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Could not fetch file URL.", severity: "error" });
    }
  };

  const handleDownload = async (file) => {
    handleMenuClose();
    const orgId = localStorage.getItem("organizationId");
    if (!orgId) return;
    try {
      const res = await downloadOrganizationFile(orgId, file.id);
      if (res && res.error) {
        setSnackbar({ open: true, message: res.error, severity: "error" });
      } else {
        setSnackbar({ open: true, message: "Download started.", severity: "success" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Could not download file.", severity: "error" });
    }
  };

  const handleDeleteClick = (idx) => {
    setDeletingIdx(idx);
    setConfirmOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    const file = files[deletingIdx];
    const orgId = localStorage.getItem("organizationId");
    if (!orgId || !file) return;
    try {
      const res = await deleteOrganizationFile(orgId, file.id);
      if (res && res.error) {
        setSnackbar({ open: true, message: res.error, severity: "error" });
      } else {
        setSnackbar({ open: true, message: "File deleted successfully!", severity: "success" });
        if (onRemove) onRemove(deletingIdx);
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to delete file.", severity: "error" });
    }
    setConfirmOpen(false);
    setDeletingIdx(null);
  };

  const handleDeleteCancel = () => {
    setConfirmOpen(false);
    setDeletingIdx(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  if (!files || files.length === 0) {
    return <div className="file-list-empty">No files uploaded.</div>;
  }

  return (
    <>
      <ul className="file-list">
        {files.map((file, idx) => (
          <li className="file-list-item" key={idx}>
            <PictureAsPdfIcon style={{ color: "#d32f2f", marginRight: 8 }} />
            <span className="file-list-name" style={{ wordBreak: "break-all", whiteSpace: "pre-wrap" }}>
              {file.fileName}
            </span>
            <IconButton size="small" onClick={(e) => handleMenuOpen(e, idx)} aria-label="file actions">
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={selectedIdx === idx && Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={() => handleView(file)}>
                <VisibilityIcon fontSize="small" style={{ marginRight: 8 }} /> View
              </MenuItem>
              <MenuItem onClick={() => handleDownload(file)}>
                <DownloadIcon fontSize="small" style={{ marginRight: 8 }} /> Download
              </MenuItem>
              {isAdmin && (
                <MenuItem onClick={() => handleDeleteClick(idx)}>
                  <DeleteIcon fontSize="small" style={{ marginRight: 8 }} color="error" /> Delete
                </MenuItem>
              )}
            </Menu>
          </li>
        ))}
      </ul>
      <Dialog open={confirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Are you sure you want to delete this file?</DialogTitle>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(snackbar.open)}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FileList;
